/** Prazo de cada tentativa: servidor travado vira erro ("Tentar de novo"), nunca "atualizando" para sempre. */
export const REQUEST_TIMEOUT_MS = 15_000;

/** Resposta que vale tentar de novo: o servidor pediu para esperar ou o proxy não alcançou o servidor. */
const RETRY_STATUS = new Set([429, 502, 503, 504]);

const nameOf = (e: unknown): string | null =>
  typeof e === 'object' && e != null && 'name' in e && typeof e.name === 'string' ? e.name : null;

/**
 * Falha de rede ou prazo estourado (o 500 é erro do próprio servidor: tentar de novo não ajuda). Endereço
 * inválido não é falha de rede: falha na hora, sem esperar.
 */
const isTransient = (e: unknown): boolean => {
  if (e instanceof TypeError) {
    const cause = (e as { cause?: { code?: unknown } }).cause;
    return cause?.code !== 'ERR_INVALID_URL' && !/invalid url|parse url/i.test(e.message);
  }
  return nameOf(e) === 'TimeoutError' || nameOf(e) === 'AbortError';
};

type Fetch = (url: string, init: RequestInit) => Promise<Response>;

/**
 * GET com sessão, prazo de 15 s por tentativa e uma nova tentativa (depois de 0,8 a 1,5 s) quando o
 * servidor pede para esperar (429/503), o proxy não chega nele (502/504), a rede cai ou o prazo estoura.
 * Cada tentativa tem o próprio prazo: a segunda não herda o tempo já gasto. Prazo estourado duas vezes
 * vira o erro "tempo esgotado".
 */
export async function getWithRetry(
  url: string,
  doFetch: Fetch = (u, init) => fetch(u, init),
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const once = () => doFetch(url, { credentials: 'include', signal: AbortSignal.timeout(timeoutMs) });
  try {
    const r = await once();
    if (!RETRY_STATUS.has(r.status)) return r;
  } catch (e) {
    if (!isTransient(e)) throw e;
  }
  await new Promise((ok) => setTimeout(ok, 800 + Math.random() * 700));
  try {
    return await once();
  } catch (e) {
    if (nameOf(e) === 'TimeoutError') throw new Error('tempo esgotado');
    throw e;
  }
}
